/**
 * Linked document numbers — shows "SO-2026-00012" instead of "1 linked".
 *
 * A document only stores the ids of the documents made from it (childIds).
 * Their numbers never change once saved, so each one is looked up ONCE and
 * then remembered on this device (memory + browser storage). Every document
 * list the app loads also teaches it numbers for free (see rememberDocNumbers
 * in useDocListPagination), so most numbers cost no read at all.
 *
 * Firebase cost: at most 1 read per linked document the first time it is
 * shown on this device; nothing after that. Lookups from many rows on the same
 * screen are combined into one query (up to 30 ids each).
 */
import { collection, getDocs, query, where, documentId } from 'firebase/firestore';
import { db } from '../config/firebase';
import { recordReads } from './readTelemetry';

const MAX_STORED = 5000;
const known = new Map();        // `${companyId}|${id}` -> { n: docNumber, t: tranTypeCode }
const loadedCompanies = new Set();
const missing = new Set();      // looked up, not found (deleted) — don't ask again this session

const storeKey = (companyId) => `amalerp.docNumbers.${companyId}`;
const k = (companyId, id) => `${companyId}|${id}`;

const loadStored = (companyId) => {
  if (!companyId || loadedCompanies.has(companyId)) return;
  loadedCompanies.add(companyId);
  try {
    const raw = window.localStorage.getItem(storeKey(companyId));
    if (!raw) return;
    const obj = JSON.parse(raw) || {};
    Object.keys(obj).forEach((id) => {
      const v = obj[id];
      if (Array.isArray(v) && v[0]) known.set(k(companyId, id), { n: v[0], t: v[1] || '' });
    });
  } catch (_) { /* storage unavailable — memory only */ }
};

let saveTimer = null;
const dirty = new Set();
const saveStored = (companyId) => {
  dirty.add(companyId);
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    dirty.forEach((c) => {
      try {
        const prefix = `${c}|`;
        const entries = [];
        known.forEach((v, key) => { if (key.startsWith(prefix)) entries.push([key.slice(prefix.length), [v.n, v.t]]); });
        const keep = entries.slice(-MAX_STORED); // newest last (Map keeps insertion order)
        window.localStorage.setItem(storeKey(c), JSON.stringify(Object.fromEntries(keep)));
      } catch (_) { /* storage full / unavailable — memory only */ }
    });
    dirty.clear();
  }, 1000);
};

const put = (companyId, id, docNumber, tranTypeCode) => {
  if (!companyId || !id || !docNumber) return false;
  const key = k(companyId, id);
  const old = known.get(key);
  if (old && old.n === docNumber && old.t === (tranTypeCode || '')) return false;
  known.delete(key);
  known.set(key, { n: docNumber, t: tranTypeCode || '' });
  return true;
};

/** Free: remember the numbers of documents already loaded on some screen. */
export const rememberDocNumbers = (companyId, docs) => {
  if (!companyId || !Array.isArray(docs) || docs.length === 0) return;
  loadStored(companyId);
  let changed = false;
  docs.forEach((d) => {
    if (d && d.id && d.docNumber) changed = put(companyId, d.id, d.docNumber, d.tranTypeCode) || changed;
    // A child also tells us its parent's number.
    if (d && d.parentId && d.parentDocNumber) changed = put(companyId, d.parentId, d.parentDocNumber, d.parentTranType || '') || changed;
  });
  if (changed) saveStored(companyId);
};

/** { n, t } for one id, or null when not known yet. */
export const knownDocNumber = (companyId, id) => {
  loadStored(companyId);
  return known.get(k(companyId, id)) || null;
};

// ── Batched lookup: ids asked for by many rows in the same moment share queries.
let queue = new Map(); // companyId -> Set(ids)
let flushPromise = null;
let flushResolve = null;

const runFlush = async () => {
  const batch = queue;
  queue = new Map();
  const resolve = flushResolve;
  flushPromise = null;
  flushResolve = null;
  try {
    for (const [companyId, idSet] of batch) {
      // Some may have been learned for free since they were asked for.
      const ids = [...idSet].filter((id) => !known.has(k(companyId, id)));
      for (let i = 0; i < ids.length; i += 30) {
        const chunk = ids.slice(i, i + 30);
        try {
          // eslint-disable-next-line no-await-in-loop
          const snap = await getDocs(query(collection(db, 'companies', companyId, 'transactions'), where(documentId(), 'in', chunk)));
          recordReads(companyId, snap.size || 1, 'linkedDocNumbers');
          const found = new Set();
          snap.docs.forEach((d) => {
            const data = d.data() || {};
            found.add(d.id);
            put(companyId, d.id, data.docNumber || '', data.tranTypeCode || '');
          });
          chunk.forEach((id) => { if (!found.has(id)) missing.add(k(companyId, id)); });
          saveStored(companyId);
        } catch (e) {
          console.warn('[linked numbers] lookup failed:', e && e.message);
        }
      }
    }
  } finally {
    if (resolve) resolve();
  }
};

const waiting = new Map(); // key -> promise of the lookup that will answer it

/** Make sure the numbers of these ids are known (reads only the unknown ones). */
export const fetchDocNumbers = (companyId, ids) => {
  if (!companyId || !Array.isArray(ids) || ids.length === 0) return Promise.resolve();
  loadStored(companyId);
  const waits = [];
  ids.forEach((id) => {
    if (!id) return;
    const key = k(companyId, id);
    if (known.has(key) || missing.has(key)) return;
    if (waiting.has(key)) { waits.push(waiting.get(key)); return; } // already being looked up
    if (!flushPromise) {
      flushPromise = new Promise((r) => { flushResolve = r; });
      setTimeout(runFlush, 30);
    }
    if (!queue.has(companyId)) queue.set(companyId, new Set());
    queue.get(companyId).add(id);
    const p = flushPromise.then(() => { waiting.delete(key); });
    waiting.set(key, p);
    waits.push(p);
  });
  return waits.length ? Promise.all(waits) : Promise.resolve();
};
